import React, { useState, useCallback } from "react";
import { View, Text, StyleSheet, FlatList, TextInput, TouchableOpacity, ActivityIndicator } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme } from "../../../context/ThemeContext";
import { ProductCard } from "./ProductCard";
import { useContent } from "../../../context/ContentContext";

export const ProductList = ({ products = [], onSelectProduct, loading = false, onEndReached }) => {
	const theme = useTheme();
	const styles = useStyles();
	const [searchQuery, setSearchQuery] = useState("");
	const [showSortOptions, setShowSortOptions] = useState(false);
	const [filters, setFilters] = useState({
		showSaleOnly: false,
		showInStockOnly: false,
		sortBy: "latest", // options: "latest", "priceAsc", "priceDesc", "name"
	});
	const { fetchContent } = useContent();
	const pageContent = fetchContent("productsPage");

	// Filter and sort products
	const filteredProducts = useCallback(() => {
		let result = [...products];

		// Apply search filter
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase().trim();
			result = result.filter((product) => product.title.toLowerCase().includes(query) || product.description.toLowerCase().includes(query) || (product.tags && product.tags.some((tag) => tag.toLowerCase().includes(query))));
		}

		// Apply other filters
		if (filters.showSaleOnly) {
			result = result.filter((product) => product.onSale);
		}

		if (filters.showInStockOnly) {
			result = result.filter((product) => product.inStock);
		}

		// Apply sorting
		switch (filters.sortBy) {
			case "priceAsc":
				result.sort((a, b) => {
					const priceA = a.onSale && a.salePrice ? a.salePrice : a.price;
					const priceB = b.onSale && b.salePrice ? b.salePrice : b.price;
					return priceA - priceB;
				});
				break;
			case "priceDesc":
				result.sort((a, b) => {
					const priceA = a.onSale && a.salePrice ? a.salePrice : a.price;
					const priceB = b.onSale && b.salePrice ? b.salePrice : b.price;
					return priceB - priceA;
				});
				break;
			case "name":
				result.sort((a, b) => a.title.localeCompare(b.title));
				break;
			case "latest":
			default:
				// Assuming products have a liveDate field
				result.sort((a, b) => new Date(b.liveDate) - new Date(a.liveDate));
				break;
		}

		return result;
	}, [products, searchQuery, filters]);

	const toggleFilter = (filterName) => {
		setFilters((prev) => ({
			...prev,
			[filterName]: !prev[filterName],
		}));
	};

	const toggleSortOptions = () => {
		setShowSortOptions(!showSortOptions);
	};

	const handleSelectSortOption = (option) => {
		setFilters((prev) => ({
			...prev,
			sortBy: option,
		}));
		setShowSortOptions(false);
	};

	const renderItem = ({ item }) => <ProductCard product={item} onPress={onSelectProduct} />;
	// Removed debug logging statement to prevent information exposure and performance impact.
	const renderHeader = () => (
		<View style={styles.listHeader}>
			<View style={styles.searchContainer}>
				<Ionicons name="search" size={20} color={theme.colors.textSecondary} />
				<TextInput style={styles.searchInput} placeholder={`Search ${pageContent.productItem.labelPlural.toLowerCase()}...`} placeholderTextColor={theme.colors.textSecondary} value={searchQuery} onChangeText={setSearchQuery} />
				{searchQuery ? (
					<TouchableOpacity onPress={() => setSearchQuery("")}>
						<Ionicons name="close-circle" size={20} color={theme.colors.textSecondary} />
					</TouchableOpacity>
				) : null}
			</View>

			<View style={styles.filtersRow}>
				<View style={styles.filterButtons}>
					<TouchableOpacity style={[styles.filterButton, filters.showSaleOnly && styles.filterButtonActive]} onPress={() => toggleFilter("showSaleOnly")}>
						<Ionicons name="pricetag-outline" size={16} color={filters.showSaleOnly ? "#fff" : theme.colors.textSecondary} />
						<Text style={[styles.filterButtonText, filters.showSaleOnly && styles.filterButtonTextActive]}>On Sale</Text>
					</TouchableOpacity>

					<TouchableOpacity style={[styles.filterButton, filters.showInStockOnly && styles.filterButtonActive]} onPress={() => toggleFilter("showInStockOnly")}>
						<Ionicons name="checkmark-circle-outline" size={16} color={filters.showInStockOnly ? "#fff" : theme.colors.textSecondary} />
						<Text style={[styles.filterButtonText, filters.showInStockOnly && styles.filterButtonTextActive]}>In Stock</Text>
					</TouchableOpacity>
				</View>

				<View style={styles.sortDropdown}>
					<Text style={styles.sortLabel}>Sort By:</Text>
					<TouchableOpacity style={styles.sortButton} onPress={toggleSortOptions}>
						<Text style={styles.sortButtonText}>
							{filters.sortBy === "latest" && "Latest"}
							{filters.sortBy === "priceAsc" && "Price: Low to High"}
							{filters.sortBy === "priceDesc" && "Price: High to Low"}
							{filters.sortBy === "name" && "Name"}
						</Text>
						<Ionicons name={showSortOptions ? "chevron-up" : "chevron-down"} size={16} color={theme.colors.textSecondary} />
					</TouchableOpacity>
				</View>
			</View>

			{/* Sort Options Dropdown - only show when button is pressed */}
			{showSortOptions && (
				<View style={styles.sortOptionsContainer}>
					<TouchableOpacity style={[styles.sortOption, filters.sortBy === "latest" && styles.sortOptionActive]} onPress={() => handleSelectSortOption("latest")}>
						<Text style={styles.sortOptionText}>Latest</Text>
						{filters.sortBy === "latest" && <Ionicons name="checkmark" size={16} color={theme.colors.primary} />}
					</TouchableOpacity>
					<TouchableOpacity style={[styles.sortOption, filters.sortBy === "priceAsc" && styles.sortOptionActive]} onPress={() => handleSelectSortOption("priceAsc")}>
						<Text style={styles.sortOptionText}>Price: Low to High</Text>
						{filters.sortBy === "priceAsc" && <Ionicons name="checkmark" size={16} color={theme.colors.primary} />}
					</TouchableOpacity>
					<TouchableOpacity style={[styles.sortOption, filters.sortBy === "priceDesc" && styles.sortOptionActive]} onPress={() => handleSelectSortOption("priceDesc")}>
						<Text style={styles.sortOptionText}>Price: High to Low</Text>
						{filters.sortBy === "priceDesc" && <Ionicons name="checkmark" size={16} color={theme.colors.primary} />}
					</TouchableOpacity>
					<TouchableOpacity style={[styles.sortOption, filters.sortBy === "name" && styles.sortOptionActive]} onPress={() => handleSelectSortOption("name")}>
						<Text style={styles.sortOptionText}>Name</Text>
						{filters.sortBy === "name" && <Ionicons name="checkmark" size={16} color={theme.colors.primary} />}
					</TouchableOpacity>
				</View>
			)}

			<View style={styles.resultsHeader}>
				<Text style={styles.resultsCount}>
					{filteredProducts().length} {filteredProducts().length === 1 ? pageContent.productItem.labelSingular.toLowerCase() : pageContent.productItem.labelPlural.toLowerCase()}
				</Text>
			</View>
		</View>
	);

	const renderFooter = () => {
		if (!loading) return null;

		return (
			<View style={styles.loaderContainer}>
				<ActivityIndicator size="small" color={theme.colors.primary} />
				<Text style={styles.loaderText}>Loading more {pageContent.productItem.labelPlural.toLowerCase()}...</Text>
			</View>
		);
	};

	const renderEmpty = () => (
		<View style={styles.emptyContainer}>
			<Ionicons name="basket-outline" size={50} color={theme.colors.textSecondary} />
			<Text style={styles.emptyTitle}>No {pageContent.productItem.labelPlural.toLowerCase()} Found</Text>
			<Text style={styles.emptySubtitle}>Try adjusting your search or filters to find what you're looking for.</Text>
		</View>
	);

	return (
		<FlatList
			data={filteredProducts()}
			renderItem={renderItem}
			keyExtractor={(item) => item.id}
			numColumns={2}
			columnWrapperStyle={styles.columnWrapper}
			contentContainerStyle={styles.listContent}
			ListHeaderComponent={renderHeader}
			ListFooterComponent={renderFooter}
			ListEmptyComponent={renderEmpty}
			onEndReached={onEndReached}
			onEndReachedThreshold={0.5}
		/>
	);
};

const useStyles = () => {
	const theme = useTheme();

	return StyleSheet.create({
		listContent: {
			padding: theme.spacing.md,
			paddingBottom: theme.spacing.xl,
		},
		columnWrapper: {
			justifyContent: "space-between",
		},
		listHeader: {
			marginBottom: theme.spacing.md,
		},
		searchContainer: {
			flexDirection: "row",
			alignItems: "center",
			backgroundColor: theme.colors.systemBackground.secondary,
			borderRadius: theme.borderRadius.md,
			paddingHorizontal: theme.spacing.sm,
			marginBottom: theme.spacing.md,
		},
		searchInput: {
			flex: 1,
			height: 44,
			...theme.typography.body,
			color: theme.colors.text,
			marginLeft: theme.spacing.xs,
		},
		filtersRow: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
			marginBottom: theme.spacing.md,
		},
		filterButtons: {
			flexDirection: "row",
		},
		filterButton: {
			flexDirection: "row",
			alignItems: "center",
			paddingHorizontal: theme.spacing.sm,
			paddingVertical: theme.spacing.xs,
			borderRadius: theme.borderRadius.md,
			backgroundColor: theme.colors.systemBackground.secondary,
			marginRight: theme.spacing.xs,
		},
		filterButtonActive: {
			backgroundColor: theme.colors.primary,
		},
		filterButtonText: {
			...theme.typography.footnote,
			color: theme.colors.textSecondary,
			marginLeft: 4,
		},
		filterButtonTextActive: {
			color: "#fff",
		},
		sortDropdown: {
			flexDirection: "row",
			alignItems: "center",
		},
		sortLabel: {
			...theme.typography.footnote,
			color: theme.colors.textSecondary,
			marginRight: theme.spacing.xs,
		},
		sortButton: {
			flexDirection: "row",
			alignItems: "center",
			backgroundColor: theme.colors.systemBackground.secondary,
			paddingHorizontal: theme.spacing.sm,
			paddingVertical: theme.spacing.xs,
			borderRadius: theme.borderRadius.md,
		},
		sortButtonText: {
			...theme.typography.footnote,
			color: theme.colors.text,
			marginRight: 4,
		},
		sortOptionsContainer: {
			backgroundColor: theme.colors.systemBackground.primary,
			borderRadius: theme.borderRadius.md,
			marginBottom: theme.spacing.md,
			borderWidth: 1,
			borderColor: theme.colors.separator,
			...theme.shadows.sm,
		},
		sortOption: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
			paddingHorizontal: theme.spacing.md,
			paddingVertical: theme.spacing.sm,
			borderBottomWidth: StyleSheet.hairlineWidth,
			borderBottomColor: theme.colors.separator,
		},
		sortOptionActive: {
			backgroundColor: theme.colors.systemBackground.secondary,
		},
		sortOptionText: {
			...theme.typography.subhead,
			color: theme.colors.text,
		},
		resultsHeader: {
			marginBottom: theme.spacing.sm,
		},
		resultsCount: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
		},
		loaderContainer: {
			flexDirection: "row",
			justifyContent: "center",
			alignItems: "center",
			padding: theme.spacing.md,
		},
		loaderText: {
			...theme.typography.footnote,
			color: theme.colors.textSecondary,
			marginLeft: theme.spacing.xs,
		},
		emptyContainer: {
			padding: theme.spacing.xl,
			alignItems: "center",
			justifyContent: "center",
		},
		emptyTitle: {
			...theme.typography.title3,
			color: theme.colors.text,
			fontWeight: "600",
			marginTop: theme.spacing.md,
			marginBottom: theme.spacing.xs,
		},
		emptySubtitle: {
			...theme.typography.body,
			color: theme.colors.textSecondary,
			textAlign: "center",
		},
	});
};
